/**
 * SecurityShield — wraps the entire app with leak-protection measures.
 *
 * What it does:
 *  A) Persoonlijk diagonaal watermerk over alle pagina's (naam+email+IP+tijd)
 *  B) Anti-kopieer: rechtermuisknop, tekstselectie op gevoelige gebieden,
 *     drag-to-save afbeeldingen, Ctrl+P/Ctrl+S/Ctrl+C op .protected blokken
 *  C) PrintScreen-detectie (waar mogelijk in browser), DevTools-detectie
 *  E) Audit-logging van page-views en security-events naar backend
 *
 * Het schermt 95% van de leak-pogingen af en maakt overgebleven lekken
 * herleidbaar (watermerk). Een vastberaden persoon met een tweede telefoon
 * kan natuurlijk altijd het scherm fotograferen — dat is fysiek onmogelijk
 * te blokkeren in een webapp.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper: token voor security-API
const tokenHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function SecurityShield({ user, children }) {
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const eventBuffer = useRef([]); // batch event-flushes naar backend
  const flushTimer = useRef(null);

  // Laad watermerk-info éénmalig bij login
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get(`${API}/api/security/me/info`, { headers: tokenHeaders() })
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => { /* niet kritisch */ });
    return () => { cancelled = true; };
  }, [user]);

  // Log een security-event (gebufferd, max 1 request per 800ms)
  const logEvent = (event_type, details = {}) => {
    eventBuffer.current.push({
      event_type,
      path: location.pathname,
      details,
    });
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => {
      const batch = eventBuffer.current.splice(0);
      batch.forEach((e) => {
        api.post(`${API}/api/security/audit/security-event`, e, { headers: tokenHeaders() })
          .catch(() => { /* nooit blokkerend */ });
      });
    }, 800);
  };

  const warn = (msg, evtType, details = {}) => {
    // Stille logging — geen zichtbare waarschuwing meer (gebruiker hoeft het niet te merken)
    logEvent(evtType, details);
  };

  // Page-view logging (route-change)
  useEffect(() => {
    if (!user) return;
    api.post(`${API}/api/security/audit/page-view`, {
      path: location.pathname,
      title: document.title || '',
      referrer: document.referrer || '',
    }, { headers: tokenHeaders() }).catch(() => { });
  }, [location.pathname, user]);

  // B) Rechter-muisknop blokkeren (behalve in inputs zodat copy-paste in forms blijft werken)
  useEffect(() => {
    if (!user) return;
    const onContextMenu = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target.isContentEditable) return;
      e.preventDefault();
      warn('Rechtsklik is uitgeschakeld om WaszBase-content te beschermen.', 'right_click');
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, [user, location.pathname]);

  // B) Drag van afbeeldingen blokkeren
  useEffect(() => {
    if (!user) return;
    const onDragStart = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'img') {
        e.preventDefault();
        logEvent('drag_image', { src: (e.target.src || '').slice(0, 200) });
      }
    };
    document.addEventListener('dragstart', onDragStart);
    return () => document.removeEventListener('dragstart', onDragStart);
  }, [user, location.pathname]);

  // B + C) Toetscombinaties: Ctrl+P (print), Ctrl+S (save), Ctrl+Shift+I (devtools),
  // F12 (devtools), Ctrl+U (source), Ctrl+Shift+C (inspector), PrintScreen
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      const k = e.key || '';
      const lk = k.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;

      // PrintScreen
      if (lk === 'printscreen' || k === 'PrintScreen') {
        // We kunnen het niet blokkeren op OS-niveau, maar wel detecteren + waarschuwen.
        // Sommige browsers vuren keyup zonder keydown bij PrintScreen — vandaar log.
        warn('PrintScreen gedetecteerd. Dit is gelogd onder jouw account.', 'print_screen');
        // Probeer clipboard te legen (werkt op sommige browsers)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(' ').catch(() => { });
        }
        return;
      }

      if (ctrl && lk === 'p') {
        e.preventDefault();
        warn('Printen is uitgeschakeld in WaszBase.', 'print_attempt');
        return;
      }
      if (ctrl && lk === 's') {
        e.preventDefault();
        warn('Pagina opslaan is uitgeschakeld.', 'save_attempt');
        return;
      }
      if (ctrl && lk === 'u') {
        e.preventDefault();
        warn('Broncode bekijken is uitgeschakeld.', 'view_source_attempt');
        return;
      }
      if (k === 'F12' || (ctrl && shift && (lk === 'i' || lk === 'j' || lk === 'c'))) {
        e.preventDefault();
        warn('Developer Tools zijn uitgeschakeld.', 'devtools_keyboard');
        return;
      }
      // Mac screenshot shortcuts (Cmd+Shift+3/4/5)
      if (e.metaKey && shift && ['3', '4', '5'].includes(k)) {
        warn('Screenshot-poging gedetecteerd. Dit is gelogd onder jouw account.', 'screenshot_keyboard', { combo: k });
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup', (e) => {
      if (e.key === 'PrintScreen') {
        warn('PrintScreen gedetecteerd. Dit is gelogd onder jouw account.', 'print_screen');
      }
    });
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [user, location.pathname]);

  // B) Kopiëren via Ctrl+C in protected zones detecteren
  useEffect(() => {
    if (!user) return;
    const onCopy = (e) => {
      // Sta toe in inputs voor normale UX, log alleen content-copy in body
      const tag = (e.target.tagName || '').toLowerCase();
      if (['input', 'textarea'].includes(tag) || e.target.isContentEditable) return;
      logEvent('copy_attempt', { selection: String(window.getSelection() || '').slice(0, 200) });
    };
    document.addEventListener('copy', onCopy);
    return () => document.removeEventListener('copy', onCopy);
  }, [user, location.pathname]);

  // C) DevTools open-detectie via window-size diff
  useEffect(() => {
    if (!user) return;
    let lastWidthDelta = 0;
    let lastHeightDelta = 0;
    let warned = false;
    const interval = setInterval(() => {
      const widthDelta = window.outerWidth - window.innerWidth;
      const heightDelta = window.outerHeight - window.innerHeight;
      // Een afwijking groter dan ~160px duidt op geopende DevTools
      const devtoolsOpen = widthDelta > 160 || heightDelta > 200;
      if (devtoolsOpen && !warned) {
        warned = true;
        warn('Developer Tools detectie — dit bezoek is gelogd onder jouw account.', 'devtools_open', {
          widthDelta, heightDelta,
        });
      } else if (!devtoolsOpen && warned && widthDelta < 100 && heightDelta < 100) {
        warned = false;
      }
      lastWidthDelta = widthDelta;
      lastHeightDelta = heightDelta;
    }, 2000);
    return () => clearInterval(interval);
  }, [user, location.pathname]);

  // Watermerk-tekst opbouwen
  if (!user) return <>{children}</>;

  return <>{children}</>;
}
